/* ──────────────────────────────────────────────────────────────
 *  useSampleStore — design-space data frames & selection state
 *
 *  Central store for the tabular data produced by the compute
 *  pipeline (training + generated samples) and the user's
 *  current selection.
 * ────────────────────────────────────────────────────────────── */

import { create } from 'zustand';
import type { SampleRow, GenRow, StarRating } from '@/types/sample';

/** Snapshot of config/input values frozen at compute time for geometry rendering. */
export interface GeometryConfigSnapshot {
  coreMaterial: string;
  outerMaterial: string;
  interSleeveMaterial: string;
  weightPockets: boolean;
  minContactArea: number | null;
  calculatedMinContactArea: number | null;
  coreId: number | null;
  sleeveThickness: number;
  /** D1: linear travel radial+ from user input (mm). */
  linearTravelRadialPlus: number | null;
  /** D1: linear travel radial- from user input (mm). */
  linearTravelRadialMinus: number | null;
}

/**
 * Bushing-type-specific expected physical test values for a single
 * sample, keyed by metric name (e.g. 'Cx', 'Cz', 'Torsion', 'Conical',
 * 'Cy', 'Conical Solid'). For A1/B1/C1 these come from the backend
 * FEA→test inverse solver; for D1/BYD types they are the design value
 * passed straight through. A null value means "not computable" for
 * that metric, not "not yet fetched" — see SampleTable.tsx which is
 * the sole writer of this map.
 */
export type ExpectedTestValuesMap = Record<string, number | null>;

interface SampleState {
  /** Training / original data loaded from backend. */
  originalSamples: SampleRow[];
  /** Newly generated samples from the latest compute run. */
  generatedSamples: GenRow[];
  /** Union of originalSamples + generatedSamples (rebuilt on every update). */
  allSamples: SampleRow[];
  /** Fast lookup: generated sample ID → full row dict. */
  genRowsById: Record<string, GenRow>;

  /** Currently selected sample ID (or null). */
  currentSelectedId: string | null;
  /** Best sample ID returned by the backend after compute (or null). */
  bestSampleId: string | null;
  /** Star rating for the current sample (0 = unrated). */
  currentRating: StarRating;
  /** Last LHS subset indices used (for reproducibility). */
  lastSubsetIndices: number[];

  /** Shore-scaling baselines: sampleId → original output values at ShA 55. */
  shoreBaselines: Record<string, Record<string, number>>;

  /** Snapshot of target values at the time of the last compute run. */
  snapshotTargets: Record<string, number | null>;

  /** Frozen config/input values for geometry — only updated on compute. */
  geometryConfigSnapshot: GeometryConfigSnapshot | null;

  /**
   * Expected physical test values, keyed by sample ID. Populated by
   * SampleTable whenever the backend inverse-solver result (or D1/BYD
   * passthrough) for the currently selected sample settles. Consumed
   * by the export flow (useExportJobs) so the PDF/PPT generators get
   * the same values the UI is showing, without recomputing them.
   */
  expectedTestValuesBySample: Record<string, ExpectedTestValuesMap>;

  /* ── Actions ──────────────────────────────────────────── */
  setOriginalSamples: (rows: SampleRow[]) => void;
  setGeneratedSamples: (rows: GenRow[]) => void;
  selectSample: (id: string | null) => void;
  /** Store the backend-selected best sample ID after a compute run. */
  setBestSampleId: (id: string | null) => void;
  setRating: (rating: StarRating) => void;
  setLastSubsetIndices: (indices: number[]) => void;
  setShoreBaseline: (id: string, baselines: Record<string, number>) => void;
  /** Update a single generated row's values (e.g. after shore scaling). */
  updateGenRow: (id: string, patch: Partial<GenRow>) => void;
  /** Snapshot the target values used for the current compute run. */
  setSnapshotTargets: (targets: Record<string, number | null>) => void;
  /** Freeze config/input values so geometry is decoupled from live inputs. */
  setGeometryConfigSnapshot: (snapshot: GeometryConfigSnapshot | null) => void;
  /** Store the bushing-type-specific expected test values for a given sample ID. */
  setExpectedTestValuesForSample: (id: string, values: ExpectedTestValuesMap) => void;
  /**
   * Clear only generated samples, selection, and related state.
   * Preserves originalSamples (training data) so plots don't blank out
   * while new training data is being fetched for a different bushing type.
   */
  clearGeneratedSamples: () => void;
  clearSamples: () => void;
}

/** Merge originalSamples + generatedSamples into allSamples. */
function buildAllSamples(orig: SampleRow[], generated: GenRow[]): SampleRow[] {
  return [...orig, ...generated];
}

/** Build genRowsById lookup. */
function buildGenLookup(rows: GenRow[]): Record<string, GenRow> {
  const map: Record<string, GenRow> = {};
  for (const row of rows) {
    map[row.id] = row;
  }
  return map;
}

export const useSampleStore = create<SampleState>()((set) => ({
  originalSamples: [],
  generatedSamples: [],
  allSamples: [],
  genRowsById: {},
  currentSelectedId: null,
  bestSampleId: null,
  currentRating: 0,
  lastSubsetIndices: [],
  shoreBaselines: {},
  snapshotTargets: {},
  geometryConfigSnapshot: null,
  expectedTestValuesBySample: {},

  setOriginalSamples: (rows) =>
    set((s) => ({
      originalSamples: rows,
      allSamples: buildAllSamples(rows, s.generatedSamples),
    })),

  setGeneratedSamples: (rows) =>
    set((s) => {
      const genRowsById = buildGenLookup(rows);
      return {
        generatedSamples: rows,
        allSamples: buildAllSamples(s.originalSamples, rows),
        genRowsById,
      };
    }),

  selectSample: (id) =>
    set({
      currentSelectedId: id,
      currentRating: 0, // reset rating on new selection
    }),

  setBestSampleId: (id) => set({ bestSampleId: id }),

  setRating: (rating) => set({ currentRating: rating }),

  setLastSubsetIndices: (indices) => set({ lastSubsetIndices: indices }),

  setShoreBaseline: (id, baselines) =>
    set((s) => ({
      shoreBaselines: { ...s.shoreBaselines, [id]: baselines },
    })),

  setSnapshotTargets: (targets) => set({ snapshotTargets: targets }),

  setGeometryConfigSnapshot: (snapshot) => set({ geometryConfigSnapshot: snapshot }),

  setExpectedTestValuesForSample: (id, values) =>
    set((s) => ({
      expectedTestValuesBySample: { ...s.expectedTestValuesBySample, [id]: values },
    })),

  updateGenRow: (id, patch) =>
    set((s) => {
      const updated: GenRow[] = s.generatedSamples.map((row) =>
        row.id === id ? ({ ...row, ...patch } as GenRow) : row,
      );
      return {
        generatedSamples: updated,
        allSamples: buildAllSamples(s.originalSamples, updated),
        genRowsById: buildGenLookup(updated),
      };
    }),

  clearGeneratedSamples: () =>
    set((s) => ({
      generatedSamples: [],
      allSamples: buildAllSamples(s.originalSamples, []),
      genRowsById: {},
      currentSelectedId: null,
      bestSampleId: null,
      currentRating: 0,
      lastSubsetIndices: [],
      shoreBaselines: {},
      snapshotTargets: {},
      geometryConfigSnapshot: null,
      expectedTestValuesBySample: {},
    })),

  clearSamples: () =>
    set({
      originalSamples: [],
      generatedSamples: [],
      allSamples: [],
      genRowsById: {},
      currentSelectedId: null,
      bestSampleId: null,
      currentRating: 0,
      lastSubsetIndices: [],
      shoreBaselines: {},
      snapshotTargets: {},
      geometryConfigSnapshot: null,
      expectedTestValuesBySample: {},
    }),
}));
